<template>
  <Page class="flex flex-center games main games-slider" :style="slideOffset">
    <transition appear name="slide-fade">
      <div class="selector" v-if="userStore.isLoggedIn">
        <Slider
          :slides="slides"
          :initial-slide="currentSlide"
          @slide-change="handleSlideChange"
          :left-arrow-src="CDN + 'button-right.svg'"
          :right-arrow-src="CDN + 'button-right.svg'"
        />
      </div>
    </transition>
    <div class="description" :key="typedChars" v-html="typedChars"></div>
  </Page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import Slider from '../Layout/Slider.vue';
import { Page } from '../../composables/useLayoutComponents';
import uri from '../GamePlay/Common/data/URLsCommon';
import { useGameSetup } from '@/composables/useGameSetup';
import useMutatorTools from '../../composables/useMutatorTools';

import { useModalStore } from '@/stores/modalStore';
import { useGameStore } from '@/stores/gameStore';
import { useThemeStore } from '@/stores/themeStore';
import { useGamesRefresh } from '@/composables/useGamesRefresh';

/** Derive game-name voice-over MP3 path from a game's display name */
function gameNameAudioUrl(gameName: string): string {
  const slug = gameName.toLowerCase().replace(/\s+/g, '-');
  return `/assets/games/audio/games-${slug}.mp3`;
}

/** Derive game-description voice-over MP3 path from a game's display name */
function gameDescriptionAudioUrl(gameName: string): string {
  const slug = gameName.toLowerCase().replace(/\s+/g, '-');
  return `/assets/games/audio/game-description-${slug}.mp3`;
}

interface Game {
  name: string;
  image: string;
  sysname: string;
  short_name: string;
  url: string;
  description: string;
}

type HomeSliderSlide = Game & {
  realIndex: number;
  isPlaceholder?: boolean;
};

const { sessionStore, gamePlayStore, userStore, gameAudio } = useGameSetup();

const { characters, typedChars } = useMutatorTools();
const modalStore = useModalStore();
const gameStore = useGameStore();

const currentSlide = ref<number>(0);
const CDN = uri;

// Generation counter to invalidate stale handleSlideChange calls.
// Slider emits slide-change during its own onMounted (before Home's), and the
// first Tone.start() is slow enough that stale async calls resolve after newer ones.
let slideChangeGeneration = 0;

// Manage visibility listener cleanup
const unbindVisibilityRef = ref<null | (() => void)>(null);

// typed computed property for available games:
// If the user has junior access, hide the Word Wheel game (id 1 / sysname 'game_1')
const availableGames = computed<Game[]>(() => {
  const games = gameStore.games || [];

  if (userStore.hasJuniorAccess) {
    return games.filter(
      (game: Game) => game.sysname !== 'game_1' && game.id !== 1
    );
  }

  return games;
});

// typed computed for slide offset
const slideOffset = computed((): Record<string, number> => {
  return { '--offset': currentSlide.value };
});

// typed computed for the current game description
// Use availableGames so the description matches the filtered carousel
const currentGameDescription = computed<string | null>(
  () =>
    availableGames.value?.[currentSlide.value]?.description ??
    'no description available'
);

watch(
  currentGameDescription,
  (newVal) => {
    if (newVal) {
      characters(newVal, 35);
    }
  },
  { immediate: true }
);

const createPlaceholderSlide = (
  realIndex: number,
  position: 'pre' | 'post',
  placeholderNumber: number
): HomeSliderSlide => ({
  name: `placeholder-${position}-${placeholderNumber}`,
  image: '',
  sysname: `placeholder-${position}-${placeholderNumber}`,
  short_name: '',
  url: '',
  description: '',
  realIndex,
  isPlaceholder: true,
});

const realSlides = computed<HomeSliderSlide[]>(() =>
  availableGames.value.map((game: Game, index: number) => ({
    ...game,
    image: getSlideImage(game),
    realIndex: index,
    isPlaceholder: false,
  }))
);

// Keep a minimum of three slides so single-game home screens keep stable sizing.
const slides = computed<HomeSliderSlide[]>(() => {
  const baseSlides = realSlides.value;
  if (baseSlides.length === 0) return [];

  const missingSlides = Math.max(0, 3 - baseSlides.length);
  if (missingSlides === 0) return baseSlides;

  const preFillCount = Math.floor(missingSlides / 2);
  const postFillCount = missingSlides - preFillCount;
  const firstRealIndex = baseSlides[0].realIndex;
  const lastRealIndex = baseSlides[baseSlides.length - 1].realIndex;

  const preFillers = Array.from({ length: preFillCount }, (_, index) =>
    createPlaceholderSlide(firstRealIndex, 'pre', index)
  );
  const postFillers = Array.from({ length: postFillCount }, (_, index) =>
    createPlaceholderSlide(lastRealIndex, 'post', index)
  );

  return [...preFillers, ...baseSlides, ...postFillers];
});

async function handleSlideChange(index: number) {
  const myGeneration = ++slideChangeGeneration;

  await gameAudio.ensureAudioInitialized();

  // If a newer slide change arrived while we were awaiting, bail out —
  // this prevents the stale call from overriding the correct one.
  if (myGeneration !== slideChangeGeneration) return;

  currentSlide.value = index;

  const game = availableGames.value?.[index];
  if (game?.name) {
    gameAudio.playSpeech(gameNameAudioUrl(game.name));
  }
}

// Return a correct image path for a given game
function getSlideImage(slide: Game): string {
  return `/assets/${slide.image}`;
}

watch(currentSlide, () => {
  if (currentGameDescription.value) {
    characters(currentGameDescription.value, 35);
  }
});

// Ensure currentSlide always points at a valid game in the filtered list
watch(availableGames, (games) => {
  const length = games?.length ?? 0;

  if (length === 0) {
    currentSlide.value = 0;
    return;
  }

  if (currentSlide.value >= length) {
    currentSlide.value = 0;
    // Also reset the persisted lastClickedGame index so we don't
    // keep trying to restore an out-of-range value on future loads
    gamePlayStore.setLastClickedGame('0');
  }
});

onMounted(() => {
  sessionStore.loading = false;
  const themeStore = useThemeStore();
  gameAudio.stopAllAudio();
  gamePlayStore.setCurrentGame(null);

  // Verify ticket consistency when returning to home
  if (userStore.isLoggedIn) {
    userStore.verifyTicketConsistency();
    // Check if we need to reset daily games counter
    userStore.checkAndResetDailyGames();
  }
  themeStore.theme = {
    backgroundColor: '#f3f3f3',
    foregroundColor: '#333333',
    backgroundImage: `${uri}arcade-background.webp`,
    secondaryColor: 'black',
    buttonColor: 'green',
  };

  // Check if lastClickedGame exists and is valid for the
  // currently available (potentially filtered) games
  let lastIndex = 0;
  const games = availableGames.value;

  if (
    gamePlayStore.state.lastClickedGame !== null &&
    gamePlayStore.state.lastClickedGame !== undefined &&
    gamePlayStore.state.lastClickedGame !== ''
  ) {
    const parsed = parseInt(gamePlayStore.state.lastClickedGame, 10);
    if (!isNaN(parsed)) {
      // Only use the saved index if it falls within the
      // range of games currently visible to this user
      if (parsed >= 0 && parsed < games.length) {
        lastIndex = parsed;
      } else {
        lastIndex = 0;
        // Persist the reset so subsequent reloads don't try
        // to restore an out-of-range index (e.g. Word Wheel
        // was removed for JR access users)
        gamePlayStore.setLastClickedGame('0');
      }
    }
  }

  // Set the current slide to the resolved last index
  currentSlide.value = lastIndex;

  // Refresh games: cache-version gate + TTL SWR on mount, and on focus
  const { ensureCacheVersionSync, refreshGamesIfStale, bindVisibilityRefetch } =
    useGamesRefresh();
  ensureCacheVersionSync();
  // Fire-and-forget background refresh; UI will already show cached list
  refreshGamesIfStale();
  // Bind focus-based refresh; remember to unbind on unmount
  unbindVisibilityRef.value = bindVisibilityRefetch();

  modalStore.resetModalMessage();

  // Preload game name + description voice clips in background
  // so they're cached when user navigates carousel or enters a game
  if (games.length > 0) {
    const voiceFiles = games.flatMap((game: Game) => [
      { key: `name_${game.name}`, url: gameNameAudioUrl(game.name) },
      { key: `desc_${game.name}`, url: gameDescriptionAudioUrl(game.name) },
    ]);
    gameAudio.preloadAudioFiles(voiceFiles);
  }
});

onUnmounted(() => {
  // Stop any game-name voice clip that may still be playing
  gameAudio.stopSpeech();

  // Unbind focus-based refresh listener
  if (unbindVisibilityRef.value) {
    unbindVisibilityRef.value();
    unbindVisibilityRef.value = null;
  }
});
</script>
